import { StatusCell } from "../../components/CertificationTable";
import { ImageView } from "../../../../components/Image";
import { getInitial, isManager } from "../../../../services/utils";
import dayjs from "dayjs";
import { AttemptSelect } from "./AttemptSelect";
import { useCertification } from "../../context/certification-context";
import { useApp } from "../../../../services/app-context";
import { getFinalStatusOptions, StatusInputSelect } from "./StatusInputSelect";
import { useUser } from "../../../../services/user-context";
import { ReactComponent as CalendarIcon } from "../../../../assets/icons/calendar-2.svg";
import { isAIRecommendationEnabled, isCertificationOverallSummaryEnabled } from "../../../../integrations/posthog/featureFlags";
import { twMerge } from "tailwind-merge";
import { getLabel } from "../../utils/labels";

export const getFinalStatusOnVerdict = (verdict: boolean, certificationAssets: any) => {
  const statusList = getFinalStatusOptions(certificationAssets);
  return statusList?.find((status) => status.value === (verdict === true ? "passed" : verdict === false ? "failed" : ""));
};

export const generateAttemptList = (attemptCount: number, isManager: boolean, showManualOption: boolean, labels: any) => {
  const attempts: any[] = [];
  for (let i = 1; i <= attemptCount; i++) {
    attempts.push({ label: `${getLabel("attempt.singular", labels)} ${i}`, value: i.toString() });
  }
  if (showManualOption || isManager) {
    attempts.push({ label: "MANUAL", value: "manual" });
  }
  return attempts;
}

export const CertificationProfileView = ({
  certificationAssignment,
  attemptInfo,
  form,
  setForm,
  assignmentDetails,
  allowStatusChange,
  showManualOption,
  attemptCount,
}: {
  certificationAssignment: any;
  attemptInfo: any;
  form: any;
  setForm: any;
  assignmentDetails: any;
  allowStatusChange: boolean;
  showManualOption: boolean;
  attemptCount: number;
}) => {

  const { userInfo } = useUser() as any;
  const role = userInfo?.role;
  const { certificationAssets } = useApp();
  const labels = certificationAssets?.labels || {};
  const { selectedCertificationAttempt, setSelectedCertificationAttempt } = useCertification();

  const userName = assignmentDetails?.userName || attemptInfo?.userName;
  const firstName = userName?.split(" ")?.[0] || "";
  const lastName = userName?.split(" ")?.[1] || "";

  const isManual = selectedCertificationAttempt === "manual";
  const passedVerdict = isManual ? attemptInfo?.passedVerdict : (attemptInfo?.lastReview?.passedVerdict ?? attemptInfo?.passedVerdict);

  const aiStatus = getFinalStatusOnVerdict(attemptInfo?.aiPassedVerdict, certificationAssets);
  const showAIRecommendation = isAIRecommendationEnabled();
  const showOverallSummary = isCertificationOverallSummaryEnabled();
  const assignmentLevelReview = certificationAssets?.features?.assignmentLevelReview ?? false;
  return (
    <div className={twMerge("flex justify-between", showOverallSummary ? "items-start" : "items-center")}>
      <div className="flex items-center gap-4">
        <div className="w-[70px] h-[70px] rounded-full">
          {certificationAssignment?.user?.profileImg ? (
            <ImageView
              className="w-[70px] h-[70px] rounded-full object-cover"
              src={certificationAssignment?.user?.profileImg}
              alt="user"
            />
          ) : (
            <div className="bg-primary w-[70px] h-[70px] rounded-full text-text-secondary text-lg flex justify-center items-center font-semibold">
              {getInitial(
                firstName,
                lastName
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-base font-medium text-text2">
            {assignmentDetails?.userName || attemptInfo?.userName}
          </span>
          <div className="text-[clamp(10px,1.5vw,12px)] text-text-gray-500">
            {assignmentDetails?.certificationName || attemptInfo?.certificationName}
          </div>
          <div className="text-[clamp(10px,1.5vw,12px)] text-text-gray-500 flex items-center gap-1">
            <CalendarIcon className="w-4 h-4" />
            {dayjs(assignmentDetails?.certificationCreatedAt || attemptInfo?.certificationCreatedAt).format(
              "MM/DD/YYYY hh:mmA"
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        {!showOverallSummary && <div className="flex items-center gap-5">
          <span className={twMerge("text-primary3 w-16 text-sm font-semibold uppercase", showAIRecommendation ? "w-40" : "")}>
            Attempt:
          </span>
          <AttemptSelect
            value={`${selectedCertificationAttempt}`}
            onChange={(value) => {
              setSelectedCertificationAttempt(value);
            }}
            options={generateAttemptList(attemptCount, isManager(role), showManualOption, labels)}
          />
        </div>}
        {showAIRecommendation && <div className="flex items-center gap-5">
          <span className="text-primary3 w-40 text-sm font-semibold uppercase">
            AI Recommendation:
          </span>
          <span className="text-text2 text-black text-xs font-semibold leading-3" style={{ color: aiStatus?.textColor }}>
            {isManual ? "N/A" : aiStatus?.label || "N/A"}
          </span>
        </div>}
        <div className="flex items-center gap-5">
          <span className={twMerge("text-primary3 w-16 text-sm font-semibold uppercase", showAIRecommendation ? "w-40" : "")}>
            {getLabel("status.singular", labels)}:
          </span>
          {
            allowStatusChange  ? (
              <StatusInputSelect
                value={form?.verdict === true ? "passed" : form?.verdict === false ? "failed" : attemptInfo?.status}
                onChange={(value) => {
                  setForm({ ...form, verdict: value === "passed" ? true : false });
                }}
                certificationAssets={certificationAssets}
                className="rounded-full !h-[30px] !w-[115px]"
              />
            ) : (
              <StatusCell className="rounded-full !h-[30px]" status={assignmentLevelReview ? (form?.verdict === true ? "passed" : form?.verdict === false ? "failed" : attemptInfo?.evaluationStatus) : passedVerdict === true ? "passed" : passedVerdict === false ? "failed" : "pending"} certificationAssets={certificationAssets} />
            )
          }
        </div>
      </div>
    </div>
  );
};
